import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { baseUrl } from "../../comman/config";
import Loader from '../../components/Loader';
import { ItemName } from '../../components/ItemName';
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/solid";

// Recipe Report. An accordion of items, one open at a time, and inside an open item ONE table:
// the ingredient names down a frozen left column, then the batch's four figures repeated for each
// batch across the top. The batch columns scroll sideways under the names.
//
// One table rather than one per batch. Each batch used to carry its own copy of the ingredients
// table, so a two-batch item printed every ingredient name twice and the eye had to match them up
// by hand -- the comparison the report exists for is Recipe Qty against મળેલ across batches, which
// only works when the ingredient is one row.
//
// Nothing here is editable. Every figure is quoted from the screen that owns it:
//
//   Planned Count / RAS / Planned RM / Planned FG   Production Estimation
//   Batch Raw Qty. / Vessel Name / Kit No.          Kit Requisition
//   Recipe Qty                                      Recipe Planning
//   માંગેલ                                            Kit Requisition ("Kit Reqd Qty")
//   મળેલ / વપરાયેલ                                    Production Support
//
// Coupon Issued is the API's count for the slot (meal_master.dish_issued_api).

// The four columns repeated for every batch, in the order the sheet lays them out.
const BATCH_COLS = [
    { key: 'recipe_qty', label: 'Recipe Qty' },
    { key: 'asked',      label: 'માંગેલ' },
    { key: 'received',   label: 'મળેલ' },
    { key: 'used',       label: 'વપરાયેલ' },
];

// A quantity reads blank when it was never entered, and to 2dp when it was. 0 is a real answer
// and prints as 0.00; null/undefined/'' are not and print as nothing.
const qty = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const n = parseFloat(v);
    return isNaN(n) ? '' : n.toFixed(2);
};

// The batch's own details, printed above its four ingredient columns and spanning them.
const BATCH_META = [
    { key: 'batch',         label: 'Batch No.',      render: (b) => b.batch },
    { key: 'batch_raw_qty', label: 'Batch Raw Qty.', render: (b) => qty(b.batch_raw_qty) },
    { key: 'vessel_name',   label: 'Vessel Name',    render: (b) => b.vessel_name },
    { key: 'kit_no',        label: 'Kit No.',        render: (b) => b.kit_no },
];

// Session defaults to whichever meal the current time falls in -- same rule and the same session
// master ids the Excess and Production reports use (1 Lunch, 2 Dinner, 3 Breakfast).
const sessionForNow = () => {
    const h = new Date().getHours();
    if (h < 11) return 3;
    if (h < 16) return 1;
    return 2;
};

// The item's plan, as Kit Requisition prints it: label left, value right, in a narrow column.
const planRow = "flex flex-wrap justify-between gap-4";
const planLabel = "sm:text-sm lg:text-base font-bold";
// The freeze pane. The names column stays put while the batches scroll under it, so an ingredient
// stays readable however many batches an item ran. Its background is set per row rather than here:
// a transparent sticky cell lets the scrolling columns show through it.
//
// It stays frozen on a phone, where the wide reports drop theirs (see comman/useIsMobile). They do
// that because their frozen block is Date + Session + Menu Item -- 342-398px of a ~360-412px
// viewport, which leaves no room to scroll to. This freezes one column of ingredient names, well
// under half that, and a number is unreadable without the name beside it: on a phone, where only
// two or three figures fit at once, the freeze earns more than it costs. Capped and allowed to wrap
// below `sm` so a long name still cannot take the screen.
const stickyCol = "sticky left-0 z-20 border-r border-b px-2 py-1 max-w-[40vw] sm:max-w-none whitespace-normal sm:whitespace-nowrap";

function RecipeReport() {
    const today = new Date().toISOString().split("T")[0];
    const [date, setDate] = useState(today);
    const [session, setSession] = useState(null);
    const [sessionList, setSessionList] = useState([]);
    const [items, setItems] = useState([]);
    const [couponIssued, setCouponIssued] = useState(0);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    // One item open at a time, matching the Production Report. null = everything collapsed, which
    // is how the report opens, so a long day is a short list of names until one is picked.
    const [activeIndex, setActiveIndex] = useState(null);

    const handleClick = (index) => setActiveIndex(index === activeIndex ? null : index);

    useEffect(() => { getSessionList(); }, []);

    const getSessionList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/comman/list?table=session_master&condition=status=1`);
            const list = (response.data.data ?? []).map((s) => ({ value: s.id, label: s.name }));
            setSessionList(list);
            // Open on the current slot rather than an empty screen.
            const preset = list.find((s) => Number(s.value) === sessionForNow()) ?? null;
            setSession(preset);
            getReport(today, preset);
        } catch (e) {
            console.error('Failed to load sessions', e);
        }
    };

    // Args are passed explicitly by Submit -- state set in the same tick is still stale here.
    const getReport = async (d = date, s = session) => {
        if (!d || !s?.value) return;
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/reports/recipe_report?date=${d}&session=${s.value}`);
            setItems(response.data.data?.items ?? []);
            // A new slot renumbers the items, so an index left over from the last one would open
            // the wrong panel.
            setActiveIndex(null);
            setCouponIssued(response.data.data?.coupon_issued ?? 0);
        } catch (e) {
            console.error('Failed to load recipe report', e);
            setItems([]);
            setCouponIssued(0);
        } finally {
            setLoading(false);
            setSubmitted(true);
        }
    };

    return (
        <div className="pb-8">
            {loading && <Loader />}
            <div className="flex flex-wrap justify-between items-center gap-2 py-3 px-2 sm:px-6">
                <h1 className="text-xl sm:text-2xl font-bold">Recipe Report</h1>
                <Link to="/">
                    <button className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                </Link>
            </div>

            {/* Filter: date (defaults to today) + session, applied on Submit. */}
            <div className="flex flex-wrap items-center gap-2 px-2 sm:px-6 pb-3">
                <input
                    type="date"
                    className="p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                />
                <div className="w-40">
                    <Select
                        isClearable
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        value={session}
                        onChange={(selected) => setSession(selected)}
                        options={sessionList}
                        placeholder="Session"
                    />
                </div>
                <button
                    onClick={() => getReport(date, session)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                    Submit
                </button>
            </div>

            {/* Coupon Issued, read only: it is the count the API supplies for this date+session.
                Printed as a label/value pair, the way Kit Requisition prints its figures. */}
            <div className="flex flex-wrap items-center gap-3 px-2 sm:px-6 pb-4">
                <span className="sm:text-sm lg:text-base font-bold">Coupon Issued :</span>
                <span className="font-bold">{couponIssued}</span>
            </div>

            <div className="relative shadow-md sm:rounded-lg">
                {items.map((item, index) => (
                    <div key={item.menu_item_id} className="border-b mt-4">
                        {/* Accordion bar, as on Kit Requisition: the Gujarati name on the orange
                            band, with the English one hidden beside it so either can be searched. */}
                        <div
                            className="flex justify-between items-center p-4 cursor-pointer accordion-title bg-orange-400 border rounded"
                            onClick={() => handleClick(index)}
                        >
                            <span><ItemName item={item} /></span>
                            {index === activeIndex
                                ? <ChevronUpIcon className="w-6 h-6" />
                                : <ChevronDownIcon className="w-6 h-6" />}
                        </div>

                        {index === activeIndex && (
                            <div className="space-y-2 p-4 bg-white">
                                {/* The plan, from Production Estimation. */}
                                <div className="flex justify-between">
                                    {/* Full width on a phone so each label/value pair spreads and
                                        the four figures line up in a column; a fixed 24rem from lg,
                                        where a full-width pair would strand the value off right. */}
                                    <div className="w-full lg:w-96">
                                        <div className={planRow}>
                                            <div className={planLabel}>Planned Count:</div>
                                            <div>{qty(item.planned_count)}</div>
                                        </div>
                                        <div className={planRow}>
                                            <div className={planLabel}>RAS:</div>
                                            <div>{qty(item.ras)}</div>
                                        </div>
                                        <div className={planRow}>
                                            <div className={planLabel}>Planned RM Qty (kgs):</div>
                                            <div>{qty(item.planned_rm_qty)}</div>
                                        </div>
                                        <div className={planRow}>
                                            <div className={planLabel}>Planned FG Qty (kgs):</div>
                                            <div>{qty(item.planned_fg_qty)}</div>
                                        </div>
                                    </div>
                                </div>

                                {item.batch_nos.length === 0 ? (
                                    <p className="font-bold text-center bg-red-100 p-3 rounded text-red-700">
                                        No batches recorded for this item.
                                    </p>
                                ) : item.ingredients.length === 0 ? (
                                    <p className="font-bold text-center bg-red-100 p-3 rounded text-red-700">
                                        Please save Recipe in Recipe Planning Screen
                                    </p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        {/* Separate borders, as Raw and Master do it. A collapsed border belongs to the
                                            table grid rather than the cell, so it does not travel with
                                            the frozen column and its edge tears as rows scroll under it.
                                            Spacing 0 keeps the grid looking collapsed; each cell draws
                                            its own right and bottom edge and the table draws top and
                                            left, so no line is doubled. */}
                                        <table style={{ borderCollapse: 'separate', borderSpacing: 0 }}
                                            className="border-t border-l text-xs sm:text-sm">
                                            <tbody>
                                                {/* The batch's details, each spanning that batch's
                                                    four columns so they sit over the figures they
                                                    describe. */}
                                                {BATCH_META.map((m) => (
                                                    <tr key={m.key}>
                                                        <th className={`${stickyCol} bg-white text-right`}>{m.label}</th>
                                                        {item.batches.map((b) => (
                                                            <td key={b.batch} colSpan={BATCH_COLS.length}
                                                                className="border-r border-b px-2 py-1 text-center font-bold">
                                                                {m.render(b)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                                <tr className="bg-gray-50">
                                                    <th className={`${stickyCol} bg-gray-50`}>Ingredients</th>
                                                    {item.batches.map((b) => BATCH_COLS.map((c) => (
                                                        <th key={`${b.batch}-${c.key}`} className="border-r border-b px-2 py-1">{c.label}</th>
                                                    )))}
                                                </tr>
                                                {item.ingredients.map((ing) => (
                                                    <tr key={ing.ingredient_id}>
                                                        <th className={`${stickyCol} bg-white text-left font-normal`}>{ing.name}</th>
                                                        {item.batches.map((b) => BATCH_COLS.map((c) => (
                                                            <td key={`${b.batch}-${c.key}`} className="border-r border-b px-2 py-1 text-center whitespace-nowrap">
                                                                {qty(ing.batches[b.batch]?.[c.key])}
                                                            </td>
                                                        )))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {submitted && !loading && items.length === 0 && (
                <div className="py-6 text-center text-gray-500">No menu items for this date and session.</div>
            )}
        </div>
    );
}

export default RecipeReport;
